import { z } from "zod";
import { AVATAR_IDS, MAX_GRADE, MIN_GRADE } from "./vocab.js";
/* ------------------------------------------------------------------ */
/* sign-up and sign-in                                                  */
/* ------------------------------------------------------------------ */
/**
 * No "log in with Google" — a 7-year-old has no Google account. A kid picks a
 * name, a face, their grade and a 4-digit pin. The parent's email is the only
 * adult contact we hold, and it is what every report and consent request goes to.
 */
export const registerChildSchema = z.object({
    name: z.string().trim().min(1).max(24),
    avatarId: z.enum(AVATAR_IDS),
    grade: z.number().int().min(MIN_GRADE).max(MAX_GRADE),
    pin: z.string().regex(/^\d{4}$/, "Pin is four numbers."),
    /**
     * The only adult detail the tablet ever collects. There is deliberately no
     * parent credential here: a child setting the parent's passcode on the same
     * device they're holding is not a control, it's a formality they watched
     * being typed. The adult sets their own password from a link we email them.
     */
    parentEmail: z.string().trim().toLowerCase().email(),
});
export const loginChildSchema = z.object({
    handle: z.string().trim().min(3).max(40),
    pin: z.string().regex(/^\d{4}$/),
});
/* ------------------------------------------------------------------ */
/* ask                                                                  */
/* ------------------------------------------------------------------ */
export const askSchema = z.object({
    text: z.string().trim().min(1).max(300),
    /**
     * Continue an existing exploration. Omit to start fresh — which is what the
     * "something new" button does, and what a returning child gets by default.
     */
    threadId: z.string().uuid().optional(),
});
/* ------------------------------------------------------------------ */
/* checks                                                               */
/* ------------------------------------------------------------------ */
export const completeCheckSchema = z.object({
    /** Did they end up right? A miss that is then corrected still counts as finished. */
    correct: z.boolean(),
    /** Right on the first go. Drives the award tier and the review schedule. */
    firstTry: z.boolean(),
});
export const confirmDeliverySchema = z.object({
    /** The child's unscored word on how it went. Optional and never counted. */
    note: z.string().trim().max(120).optional(),
});
export const setGoalSchema = z.object({ rewardId: z.string().min(1) });
export const requestGoalSchema = z.object({ rewardId: z.string().min(1) });
/* ------------------------------------------------------------------ */
/* goal ideas                                                           */
/* ------------------------------------------------------------------ */
/**
 * A kid can say what they want; they cannot say what it costs. That split is
 * the whole point — pricing is the only lever a parent has over the economy,
 * and handing it to the child turns the goal list into a wish granted on
 * demand. So this carries a label and nothing else, and a grown-up attaches
 * the number before it becomes a real goal.
 */
export const suggestGoalSchema = z.object({
    text: z.string().trim().min(2).max(60),
});
export const decideSuggestionSchema = z
    .object({
    decision: z.enum(["added", "declined"]),
    cost: z.number().int().min(5).max(5000).optional(),
})
    // Adding it without a price would create a goal nobody can ever reach.
    .refine((v) => v.decision === "declined" || typeof v.cost === "number", {
    message: "Set a price before adding it.",
    path: ["cost"],
});
/* ------------------------------------------------------------------ */
/* grown-ups                                                            */
/* ------------------------------------------------------------------ */
/**
 * The parent portal is a public URL, so the credential has to survive being
 * guessed at from the internet rather than from a sofa. A four-digit pin is
 * ten thousand tries; a password plus rate limiting is a different problem.
 */
export const PARENT_PASSWORD_MIN = 8;
export const parentSetupSchema = z.object({
    token: z.string().min(16),
    password: z
        .string()
        .min(PARENT_PASSWORD_MIN, `Use at least ${PARENT_PASSWORD_MIN} characters.`)
        .max(200),
});
export const parentLoginSchema = z.object({
    email: z.string().trim().toLowerCase().email(),
    password: z.string().min(1),
});
export const parentForgotSchema = z.object({
    email: z.string().trim().toLowerCase().email(),
});
/**
 * Correcting a mistyped parent address, from the waiting screen. Only accepted
 * while no parent on the account has ever set a password — so it can never be
 * used to move a child away from a grown-up who is already watching.
 */
export const fixParentEmailSchema = z.object({
    parentEmail: z.string().trim().toLowerCase().email(),
});
/** Unlocks the small on-device screen. Returns no parent token by design. */
export const deviceUnlockSchema = z.object({
    password: z.string().min(1),
});
/**
 * Erasing a child, or the whole family.
 *
 * The password is re-entered even though the caller already holds a parent
 * token. A thirty-minute session is convenient for approving a reward and far
 * too loose for the one action in this product that cannot be undone — a
 * borrowed laptop with the portal still open should not be able to delete a
 * child's two years of stars.
 *
 * `confirm` is the account's own email address, typed out. Not security — the
 * password is that — but the difference between meaning it and clicking the
 * red thing.
 */
export const deleteAccountSchema = z.object({
    password: z.string().min(1),
    confirm: z.string().trim().toLowerCase(),
});
export const upsertRewardSchema = z.object({
    id: z.string().optional(),
    label: z.string().trim().min(1).max(60),
    cost: z.number().int().min(5).max(5000),
    emoji: z.string().max(4).default("🎁"),
});
export const decideRequestSchema = z.object({
    decision: z.enum(["approved", "denied"]),
});
//# sourceMappingURL=api.js.map